import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { Core } from '@core/core';
import { ISessionTokenClaims } from '@core/base/session-base.types';
import { environment } from '@env';
import {
    DECRYPT_ERROR_MESSAGE, MISSING_ENCRYPTED_DATA_MESSAGE, encryptedEnvelopeData,
    encryptPayload, decryptPayload,
} from '@common/transport/session-crypto';
import { MaterialError, SessionMaterialCache, TransportContext, VersionedCryptoMaterials } from '@common/transport/session-material-cache';
import { OperationAdapter, OperationAttempt, OperationFailure, TransportOperationPolicy } from '@common/transport/operation-policy';
import {
    KeySetResponse, SecureRequestOptions,
    TRANSPORT_CONTRACT_HEADER, TRANSPORT_CONTRACT_VERSION, TRANSPORT_CONTEXT_HEADER,
} from '@common/types/api/session-crypto.types';
import { officeApiRoute } from './office-api-routes';
import { officeTransportCapabilities } from './office-transport-operations';

interface SessionTokenSource
{
    check?: () => Promise<boolean>;
    getTokenClaims?: () => ISessionTokenClaims | null | undefined;
    getAccessToken?: () => string | null;
    authState$?: Observable<boolean | undefined> & { getValue(): boolean | undefined; };
    sessionInfo$?: Observable<unknown>;
}

interface OfficeTransportContext extends TransportContext { readonly sessionToken: string; }

interface PreparedRequest
{
    readonly body: unknown;
    readonly headers: HttpHeaders;
    readonly materials?: VersionedCryptoMaterials;
}

class MaterialsChangedError extends Error
{
    constructor() { super('Session encryption materials changed'); }
}

@Injectable({ providedIn: 'root' })
export class SessionCryptoService implements OnDestroy
{
    private readonly rawHttp: HttpClient;
    private readonly materialCache: SessionMaterialCache<OfficeTransportContext>;
    private boundSession?: SessionTokenSource;
    private subscriptions = new Subscription();
    private resetGeneration = 0;
    private invalidation = this.createInvalidation();

    constructor(
        private readonly http: HttpClient,
        httpBackend: HttpBackend,
        private readonly core: Core,
    )
    {
        this.rawHttp = new HttpClient(httpBackend);
        this.materialCache = new SessionMaterialCache({
            subtle: crypto.subtle,
            clock: { now: () => Date.now() },
            currentContext: () => this.currentContext(),
            exchange: async (context, request) => {
                const response = await firstValueFrom(this.rawHttp.post<KeySetResponse>(
                    `${context.apiEnvironment}/api/secure/session/key/set`, request,
                    { headers: this.headers(context).set(TRANSPORT_CONTRACT_HEADER, TRANSPORT_CONTRACT_VERSION) },
                ));
                // The shared cache validates metadata and fences late results before adoption.
                return { public_key: response?.public_key, transportVersion: response?.transport_context_version! };
            },
        });
    }

    public reset(): void
    {
        this.resetGeneration++;
        this.materialCache.reset();
        this.invalidation.resolve();
        this.invalidation = this.createInvalidation();
    }

    public ngOnDestroy(): void
    {
        this.reset();
        this.subscriptions.unsubscribe();
    }

    public async encryptData(body: unknown): Promise<string>
    {
        if (body == null) return '';
        this.bindSession();
        const materials = await this.ensureMaterials();
        const data = await encryptPayload(body, materials, crypto.subtle);
        this.requireMaterials(materials);
        return data;
    }

    public async decryptData<T = Record<string, unknown>>(hexData?: string | null): Promise<T>
    {
        if (!hexData) return {} as T;
        this.bindSession();
        const materials = await this.ensureMaterials();
        const result = await decryptPayload<T>(hexData, materials, crypto.subtle);
        this.requireMaterials(materials);
        return result;
    }

    public async postSecure<TRequest = unknown, TResponse = Record<string, unknown>>(
        path: string, body: TRequest, options?: SecureRequestOptions,
    ): Promise<TResponse>
    {
        return this.execute('POST', path, body, options, 'secure');
    }

    public async getSecure<TResponse = Record<string, unknown>>(path: string, options?: SecureRequestOptions): Promise<TResponse>
    {
        return this.execute('GET', path, undefined, options, 'secure');
    }

    public postAddin<TRequest = unknown, TResponse = Record<string, unknown>>(
        path: string, body: TRequest, options?: SecureRequestOptions,
    ): Promise<TResponse>
    {
        return this.execute('POST', path, body, options);
    }

    public getAddin<TResponse = Record<string, unknown>>(path: string, options?: SecureRequestOptions): Promise<TResponse>
    {
        return this.execute('GET', path, undefined, options);
    }

    /** Explicit HTTPS-only operations; this entry point cannot downgrade a configured secure operation. */
    public postAddinPlain<TRequest = unknown, TResponse = Record<string, unknown>>(
        path: string, body: TRequest, options?: SecureRequestOptions,
    ): Promise<TResponse>
    {
        return this.execute('POST', path, body, options, 'https');
    }

    private async execute<T>(
        method: 'GET' | 'POST', path: string, body: unknown, options?: SecureRequestOptions,
        onlyTransport?: 'secure' | 'https',
    ): Promise<T>
    {
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        const policy = new TransportOperationPolicy(officeTransportCapabilities(onlyTransport === 'secure' || environment.secure_transport_enabled));
        // Capture caller-owned input before waiting for keys; HTTP dispatch never consults a new session.
        const payload: unknown = body == null ? body : JSON.parse(JSON.stringify(body));
        const extraHeaders = { ...options?.headers };
        const retryAttempted = options?.retryAttempted === true;
        this.bindSession();
        let context = this.currentContext();
        if (!context && this.boundSession?.check)
        {
            if (await this.boundSession.check()) context = this.currentContext();
        }
        const lifetime = {
            cancelled: this.invalidation.promise,
            isCurrent: () => this.sameContext(context, this.currentContext()),
        };
        const makeAdapter = (secure: boolean): OperationAdapter<PreparedRequest, unknown, T> => ({
            prepare: async attempt => {
                if (!context) throw new Error('Office session is required');
                let headers = this.headers(context, extraHeaders);
                if (!secure) return { body: payload, headers };
                const materials = await this.ensureMaterials();
                this.requireAttempt(attempt);
                const data = method === 'POST' ? await encryptPayload(payload, materials, crypto.subtle) : undefined;
                this.requireAttempt(attempt);
                this.requireMaterials(materials);
                headers = headers.set(TRANSPORT_CONTRACT_HEADER, TRANSPORT_CONTRACT_VERSION)
                    .set(TRANSPORT_CONTEXT_HEADER, materials.transportVersion);
                return { body: method === 'POST' ? { data } : undefined, headers, materials };
            },
            dispatch: async (request, attempt) => {
                this.requireAttempt(attempt);
                if (request.materials) this.requireMaterials(request.materials);
                const url = `${context!.apiEnvironment}${officeApiRoute(method, normalizedPath, secure)}`;
                try
                {
                    return await firstValueFrom(method === 'POST'
                        ? this.http.post<unknown>(url, request.body, { headers: request.headers })
                        : this.http.get<unknown>(url, { headers: request.headers }));
                }
                catch (error)
                {
                    if (this.isContextConflict(error)) this.invalidateMaterials(request.materials);
                    throw error;
                }
            },
            decode: async (response, request, attempt) => {
                if (!request.materials) return response as T;
                try
                {
                    const result = await decryptPayload<T>(encryptedEnvelopeData(response), request.materials, crypto.subtle);
                    this.requireAttempt(attempt);
                    this.requireMaterials(request.materials);
                    return result;
                }
                catch (error)
                {
                    if (this.isDecodeFailure(error)) this.invalidateMaterials(request.materials);
                    throw error;
                }
            },
            recover: (failure: OperationFailure) => secure && !retryAttempted
                && (failure.cause instanceof MaterialsChangedError
                    || (failure.stage === 'dispatch' && this.isContextConflict(failure.cause))
                    || (failure.stage === 'decode' && this.isDecodeFailure(failure.cause))),
        });
        return policy.execute(`${method} ${normalizedPath}`, {
            secure: onlyTransport === 'https' ? undefined : makeAdapter(true),
            https: onlyTransport === 'secure' ? undefined : makeAdapter(false),
        }, lifetime);
    }

    private async ensureMaterials(): Promise<VersionedCryptoMaterials>
    {
        try { (await this.materialCache.prepare())(); }
        catch (error)
        {
            const failure = error as MaterialError;
            if (failure?.code === 'exchange-failed' && failure.cause instanceof HttpErrorResponse) throw failure.cause;
            throw error;
        }
        const materials = this.materialCache.getMaterials();
        if (!materials) throw new Error('Current session encryption materials are required');
        return materials;
    }

    private requireMaterials(materials: VersionedCryptoMaterials): void
    {
        const current = this.materialCache.getMaterials();
        if (!current || current.key !== materials.key || current.transportVersion !== materials.transportVersion)
            throw new MaterialsChangedError();
    }

    private invalidateMaterials(materials?: VersionedCryptoMaterials): void
    {
        // A late failed response must not clear the keys established by a newer request.
        if (materials && this.materialCache.getMaterials()?.key === materials.key) this.materialCache.reset();
    }

    private requireAttempt(attempt: OperationAttempt): void
    {
        if (!attempt.isCurrent()) throw new Error('Office transport operation cancelled');
    }

    private headers(context: OfficeTransportContext, extra?: Record<string, string>): HttpHeaders
    {
        return new HttpHeaders(extra).delete(TRANSPORT_CONTRACT_HEADER).delete(TRANSPORT_CONTEXT_HEADER)
            .delete('X-Session-Id').delete('X-Device-Hash').delete('Authorization')
            .set('Authorization', `Bearer ${context.sessionToken}`);
    }

    private isContextConflict(error: unknown): boolean
    {
        return error instanceof HttpErrorResponse && error.status === 409
            && error.error?.code === 'SESSION_TRANSPORT_CONTEXT_CHANGED';
    }

    private isDecodeFailure(error: unknown): boolean
    {
        return error instanceof Error && (error.message === DECRYPT_ERROR_MESSAGE || error.message === MISSING_ENCRYPTED_DATA_MESSAGE);
    }

    /** Adapt existing Office notifications; authentication/recovery remain owned by OfficeSessionService. */
    private bindSession(): void
    {
        const source = this.core.session as unknown as SessionTokenSource | undefined;
        if (this.boundSession === source) return;
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
        this.boundSession = source;
        this.reset();
        this.subscriptions.add(source?.authState$?.subscribe(authenticated => { if (authenticated !== true) this.reset(); }));
        this.subscriptions.add(source?.sessionInfo$?.subscribe(() => this.reset()));
    }

    private currentContext(): OfficeTransportContext | null
    {
        const source = this.core.session as unknown as SessionTokenSource | undefined;
        if (!source || (source.authState$ && source.authState$.getValue() !== true)) return null;
        const sessionToken = source.getAccessToken?.();
        const claims = source.getTokenClaims?.();
        if (!sessionToken) return null;
        const identity = sessionToken;
        return {
            apiEnvironment: environment.office_api_host.replace(/\/$/, ''), identity,
            sessionId: sessionToken, sessionToken,
            generation: this.resetGeneration, transportVersion: null,
            expiresAt: typeof claims?.exp === 'number' ? claims.exp * 1000 : null,
        };
    }

    private sameContext(left: OfficeTransportContext | null, right: OfficeTransportContext | null): boolean
    {
        return !!left && !!right && left.apiEnvironment === right.apiEnvironment && left.identity === right.identity
            && left.sessionId === right.sessionId && left.generation === right.generation
            && (left.expiresAt === null || Date.now() < left.expiresAt)
            && (right.expiresAt === null || Date.now() < right.expiresAt);
    }

    private createInvalidation(): { promise: Promise<void>; resolve(): void; }
    {
        let resolve!: () => void;
        const promise = new Promise<void>(done => { resolve = done; });
        return { promise, resolve };
    }
}
